/**
 * חברות בארגון — company administration, and the refusal at the limit. OA-0001 §3.11, 08 §7.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * AT THE LIMIT, THE COUNT · THE LIMIT · A ROUTE. NEVER A GENERIC ERROR.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 08 §7 requires a hard block to return *"a SPECIFIC ACTIONABLE reason with the count, the limit and
 * an upgrade path — never a generic 403, never a silent failure"*, and OA-0001 §3.11 adds the half
 * that matters more: *"Commercial enforcement must not corrupt or delete existing Company data."*
 * Both halves are on this screen, and the second one is written out in the refusal itself — because
 * an office told only "you cannot add a company" reasonably fears that something is about to be
 * taken away.
 *
 * The refusal is a value the SERVER produced (`checkCapacity` in `apps/api/src/domain/capacity.ts`).
 * This screen renders it. It does not compare `current` to `limit` to decide anything: a second
 * implementation of a commercial rule is family F9 with a Hebrew accent, and F9 is two answers to
 * one question drifting apart at their own pace.
 *
 * ── AND THE BUTTON IS STILL THERE ───────────────────────────────────────────────────────────────
 *
 * "הקמת חברה" is not hidden at the limit. A hidden button is presentation, never a control — the
 * gate refuses the write regardless, and hiding the affordance only means the office cannot find out
 * WHY. What changes at the limit is that the refusal is stated before the form is opened rather than
 * after it is filled in.
 */

import { useCallback } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { CapacityRefusalNotice } from '../components/CapacityPanel';
import { DataTable } from '../components/DataTable';
import { LifecycleChip } from '../components/LifecycleChip';
import { ServerRead } from '../components/ServerRead';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { CompanySummary } from '../api/contract';
import {
  fixtureCompanyCapacityRefusal,
  fixtureSubscription,
  fixturesEnabled,
} from '../api/fixtures';
import { routes } from '../routes';


export function TenantCompaniesScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();

  /*
   * ── THE SURFACE GUARD MOVED BELOW THE HOOKS. **2026-11-03.** ─────────────────────────────────
   *
   * It used to stand here, and `useCallback` and `useServerData` were declared ninety lines below
   * it — so a render taken while the session context had not yet resolved to `TENANT` ran two
   * hooks fewer than the render after it. That is React error #310, *"Rendered more hooks than
   * during the previous render"*, and it is the exact crash the Owner met on `TenantUserScreen`
   * the same day. This screen carried it silently, waiting for a slower session read.
   *
   * THE GUARD MOVED RATHER THAN THE HOOKS, because the hooks depend on `capabilities`, which is
   * derived between the two. Moving the guard keeps one order; moving the hooks would have meant
   * moving their inputs with them.
   *
   * WHAT RUNS ON A PLATFORM SURFACE NOW: the capability derivations, which read a session that is
   * already loaded, and `useServerData(readRegister)` — whose callback is guarded on
   * `companies.company.manage`. A platform session does not hold that office capability, so the
   * callback resolves locally from the context and NO REQUEST IS MADE. The notice below is
   * returned exactly as before.
   */

  /*
   * THE READ, NOT THE LIST — AND THE DIFFERENCE IS A SENTENCE ABOUT THE CUSTOMER'S OWN CLIENTS.
   *
   * This was `ctx.visibleCompanies`, rendered straight into the table. A list cannot say whether it
   * is empty because the office has no clients or because the read was refused, so a refusal printed
   * *"there are no companies in the office yet"* — a statement of fact about the firm's own book of
   * business, produced by a request that never answered. An office reading that goes looking for who
   * deleted its clients.
   *
   * `companiesResult` exists on the context for exactly this (`ContextProvider.tsx:154-167`) and
   * `CompanyPickerScreen` already renders it through `<ServerRead>`. The same mechanism, applied
   * here. The genuine zero-state — which M:704 makes valid data, not an error — stays where it
   * belongs, inside the table's `emptyStateHe`, and no new sentence is written.
   *
   * `visibleCompanies` is unchanged and every other consumer of it is unaffected. There is still one
   * source: the list is DERIVED from this result by whoever supplies both.
   */

  /*
   * ── THIS SCREEN READS THE REGISTER, NOT THE GRANT-FILTERED LIST. **Section 1, 2026-10-25.** ───
   *
   * The Owner created a company, was told it was created, and then saw *"there are no companies in
   * the office"* here, no companies to grant on `/tenant/access`, and 0 on the home counter.
   *
   * All three read `companies.list.read`, which INNER JOINs `core.user_company_access`. A company
   * created a minute ago has no access row for anybody -- so it was invisible to everyone,
   * including the person who had just created it, and it could not be granted either, because the
   * grant screen offers the same filtered list. Granting required seeing; seeing required a grant.
   *
   * `companies.register.read` is the office's own register, requires the capability that CREATES
   * companies, and makes nothing openable -- opening a company still passes gate stage 4, where the
   * grant is required and is unchanged. The grant filter stays exactly as it was for the work
   * surface: the context bar and the company picker are untouched.
   */
  /*
   * ══ TWO LISTS, AND THE SESSION DECIDES WHICH ONE THIS IS. **Owner acceptance, 2026-10-28.** ══
   *
   * Reported: *"ליפית נשמרה הרשאת צפייה ועריכה לחברת הבדיקה — רואים זאת בכרטיס המשתמש.
   * למרות זאת, לחברות שלי מציג חוסר הרשאה ולא מציג את החברה."* And: *"אין לפתור זאת בהענקת
   * הרשאות ניהול רחבות."*
   *
   * THE SERVER WAS RIGHT AND THE SCREEN ASKED THE WRONG QUESTION. There are two routes, and the
   * difference between them is the whole separation:
   *
   *   `companies.list.read`      cap `companies.company.read`    GRANT-FILTERED — *which companies
   *                                                              may I open*
   *   `companies.register.read`  cap `companies.company.manage`  THE OFFICE'S REGISTER — *which
   *                                                              companies does this office have*
   *
   * This screen called the REGISTER unconditionally. For an administrator that is right: the
   * register exists so a company created a minute ago, with no access row for anybody yet, is
   * visible to the person who must grant access to it. For somebody holding a GRANT and no
   * management capability it is the wrong question, and the gate refused it — correctly. The
   * refusal was not the defect; asking was.
   *
   * So the grant-filtered answer is used when the session cannot manage. It is ALREADY LOADED:
   * `<App>` reads `companies.list.read` on every tenant load and it is `ctx.visibleCompanies`.
   * Nothing is widened, no capability is granted, and opening any of these still passes gate
   * stage 4 where the grant itself is checked.
   */
  const capabilities = ctx.session?.capabilities ?? [];
  const mayManage = capabilities.includes('companies.company.manage');
  const mayCreate = capabilities.includes('companies.company.create');
  const mayReadPlan = capabilities.includes('billing.plan.read');

  const readRegister = useCallback(
    () =>
      mayManage
        ? callOperation('companies.register.read', {})
        : // Not called at all without the capability. A request made in order to be refused is a
          // denial the reader has to read, for a question they never asked.
          Promise.resolve({ companies: ctx.visibleCompanies }),
    [mayManage, ctx.visibleCompanies],
  );
  const registerRead = useServerData(readRegister);

  /* THE GUARD, now below every hook this component declares. See its note above the reads. */
  if (ctx.surface !== 'TENANT') {
    return (
      <SurfaceNotice titleHe={t('settings.e.companies')} requires="TENANT" actual={ctx.surface} />
    );
  }

  // The refusal, as the server produced it. `null` when there is room, and the screen shows nothing.
  const refusal = fixturesEnabled() ? fixtureCompanyCapacityRefusal : null;

  const columns: readonly Column<CompanySummary>[] = [
    {
      key: 'name',
      headerHe: t('company.name'),
      render: (c) => (
        <span className="company-cell">
          <span className="company-cell__name">{c.legalName}</span>
          <span className="company-cell__id" dir="ltr">
            {c.registrationNumber ?? t('company.noNumber')}
          </span>
        </span>
      ),
    },
    {
      key: 'lifecycle',
      headerHe: t('company.state'),
      render: (c) => <LifecycleChip status={c.lifecycleStatus} />,
    },
    {
      key: 'attention',
      headerHe: t('common.notes'),
      render: (c) => (c.needsAttention ? <span className="chip chip--attention">{t('company.needsAttention')}</span> : null),
    },
    {
      key: 'record',
      headerHe: t('company.cardLink'),
      /*
        THE ADMINISTRATIVE RECORD, which is a different place from the work surface.
        The row itself opens `/c/{uid}` — where a bookkeeper does this month's work. This column
        opens the client's master record: its legal name, its lifecycle state, its identifiers and
        its source bindings. Keeping them apart is what stops "mark this client as former" from
        sitting one click from "open the trial balance".
      */
      render: (c) => (
        <a className="btn btn--quiet" href={routes.tenantCompany(c.companyUid)}>
          {t('company.detailsAndState')}
        </a>
      ),
    },
  ];

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('settings.e.companies')}
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
    >
      {/* The refusal comes FIRST when there is one: it is the answer to the action the user came for. */}
      {refusal === null ? null : <CapacityRefusalNotice refusal={refusal} />}

      {/*
        ── IT LOOKS LIKE A BUTTON NOW, AND IT IS STILL A LINK. **§3, 2026-10-25.** ────────────────

        Owner: *"כפתור הקמת חברה יושב ממש על התיבה של המכסה שנרכשה, זה לא נראה ככפתור זה נראה כמו
        טאב"*. Two causes, and neither of them was this line:

          · `.actions-row` had `margin-block-start` and no end, so the row sat flush against
            `CapacityPanel` — fixed in `styles.css`;
          · `.btn--nav` was a transparent box with a grey border and inherited weight, which is the
            silhouette of a tab — also fixed in `styles.css`, in the brand teal, with weight.

        ── AND IT IS **NOT** `btn--primary`, WHICH IS WHAT I TRIED FIRST ─────────────────────────

        `components/actionAffordance.test.ts` rule 2 refused it: *"NAVIGATION IS NOT AN ACTION — no
        `<a>` carries `btn--primary`"*. That control exists because of the Owner's OWN finding of
        2026-10-20 — *"״מנוי ומכסות״ בכרטיס הלקוח נתפס כלשונית ולא כפעולה"* — and the fix then was
        that a filled button means A WRITE and an outlined one means a DESTINATION. `הקמת חברה`
        opens a form; it writes nothing. Giving it the write treatment would have made going
        somewhere and changing the estate the same shape again, which is the thirty-link defect that
        test was written to close.

        So the treatment stayed `btn--nav` and the TREATMENT was fixed.
      */}
      {/*
        EACH CONTROL APPEARS WHEN ITS CAPABILITY IS HELD, AND NOT OTHERWISE. *"מידע על המנוי
        ופעולות ניהול יוצג רק למורשים."* A link that leads to a refusal is not information.
      */}
      {mayCreate || mayReadPlan ? (
        <div className="actions-row">
          {mayCreate ? (
            <a className="btn btn--nav" href={routes.tenantCompanyOnboarding}>
              {t('company.create')}
            </a>
          ) : null}
          {mayReadPlan ? (
            <a className="btn" href={routes.tenantSubscription}>
              {t('settings.e.subscription')}
            </a>
          ) : null}
        </div>
      ) : null}

      {/*
        ── THE QUOTA PANEL IS GONE FROM THIS SCREEN. **Owner, 2026-10-28.** ──────────────────────
        *"במסך החסום מוצג גם לא נרשמו מכסות למנוי הזה. בדוק שההודעה מבוססת על קריאה
        מוצלחת ומורשת: כשל טעינה או הרשאה אינו אומר שאין מכסות."*

        He is right, and it was worse than a wrong message: **there was no read at all.** The rows
        came from `fixturesEnabled() ? fixtureSubscription.capacity : []`, so on a real server the
        value was ALWAYS the empty array — and `CapacityPanel` turns an empty array into the
        sentence *לא נרשמו מכסות למנוי הזה*. A statement about the subscription, rendered from
        nothing, on a screen that never asked.

        The quotas live on the subscription screen, which reads them through `<ServerRead>` and is
        reached by the link above when the capability is held. `capacityPanelHasARead.test.ts`
        keeps it that way.
      */}

      <ServerRead result={registerRead.result} whatHe={t('companies.reading')} skeletonRows={8}>
        {(data) => (
          <DataTable<CompanySummary>
            captionHe={t('settings.e.companies')}
            columns={columns}
            rows={data.companies}
            rowKey={(c) => c.companyUid}
            childHref={(c) => routes.companyHome(c.companyUid)}
            filterSummaryHe={`${t('companies.showing')}: ${String(data.companies.length)}`}
            emptyStateHe={t('companies.empty')}
          />
        )}
      </ServerRead>
    </AppShell>
  );
}
